"use client"

import { React, useEffect, useState } from "react"
import { Card, CardContent } from "./ui/card"
import { DollarSign, Calendar, Clock, RefreshCw, Filter, ChevronDown, ChevronUp, Search } from "lucide-react"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Link } from "react-router-dom"
import { Badge } from "./ui/badge"
import { useFetchActions } from "../hooks/DataFetching/useFetchActions"

export const FetchActions = ({ groupId, campaignId, selectedActionId }) => {
  const [showFilters, setShowFilters] = useState(false)
  const [searchInputValue, setSearchInputValue] = useState("")

  const {
    actions,
    loading,
    error,
    statusFilter,
    sortBy,
    sortOrder,
    currentPage,
    totalPages,
    pageSize,
    handleSearch,
    handleStatusFilterChange,
    handleSortChange,
    handlePageChange,
    handlePageSizeChange,
    applyFilters,
  } = useFetchActions({ groupId, campaignId })

  useEffect(() => {
    if (!selectedActionId || !actions?.length) return
    window.requestAnimationFrame(() => document.getElementById(`group-action-${selectedActionId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }))
  }, [actions, selectedActionId])

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    handleSearch(searchInputValue)
  }

  const formatDate = (dateString) => {
    if (!dateString) return "Not set"
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString()
    } catch (e) {
      return "Invalid Date"
    }
  }

  const formatCurrency = (amountString) => {
    if (!amountString) return "$0"
    try {
      const amount =
        typeof amountString === "string" ? Number.parseFloat(amountString.replace(/[^0-9.-]+/g, "")) : amountString
      return amount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      })
    } catch (e) {
      return "$0"
    }
  }

  const getStatusBadge = (status) => {
    let badgeVariant = "secondary"

    if (status === "Todo") {
      badgeVariant = "outline"
    } else if (status === "InProgress") {
      badgeVariant = "default"
    } else if (status === "Refining") {
      badgeVariant = "purple" // Custom variant - you may need to add this to your Badge component
    } else if (status === "Completed") {
      badgeVariant = "success" // Custom variant - you may need to add this to your Badge component
    }

    return <Badge variant={badgeVariant}>{status}</Badge>
  }

  const renderSortIcon = (field) => {
    if (sortBy !== field) return null
    return sortOrder === "asc" ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
  }

  const renderActionCard = (action) => (
    <Card id={`group-action-${action.id}`} key={action.id} className={`mb-4 hover:shadow-md transition-shadow ${String(action.id) === String(selectedActionId) ? "group-linked-record" : ""}`}>
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-3">
          <h4 className="font-medium text-lg">{action.actionStatement}</h4>
          {getStatusBadge(action.actionStatus)}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center">
            <DollarSign className="h-4 w-4 mr-1 text-gray-500" />
            <span className="font-medium mr-1">Funding:</span>
            {formatCurrency(action.fundingAllocated)}
          </div>

          <div className="flex items-center">
            <Calendar className="h-4 w-4 mr-1 text-gray-500" />
            <span className="font-medium mr-1">Execution:</span>
            {formatDate(action.executionDate)}
          </div>

          <div className="flex items-center">
            <Clock className="h-4 w-4 mr-1 text-gray-500" />
            <span className="font-medium mr-1">Created:</span>
            {formatDate(action.createdOn)}
          </div>
        </div>

        {(action.campaignId || action.groupId) && (
          <div className="mt-3 text-sm text-gray-500">
            {action.campaignId && <span className="mr-3">Campaign ID: {action.campaignId}</span>}
            {action.groupId && <span>Group ID: {action.groupId}</span>}
          </div>
        )}

        {/* Add refinement link for actions in "Refining" status */}
        {action.actionStatus === "Refining" && (
          <div className="mt-4 flex justify-end">
            <Link to={`/actions/${action.id}/refinement`}>
              <Button size="sm">
                <RefreshCw className="h-3 w-3 mr-1" /> Refine Action
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  )

  const renderPagination = () => {
    if (totalPages <= 1) return null

    return (
      <div className="flex justify-between items-center mt-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Items per page:</span>
          <select
            value={pageSize.toString()}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            className="w-16 h-8 rounded-md border border-gray-300 px-2 py-1 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>

        <div className="flex items-center space-x-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pageNum = i + 1
            return (
              <Button
                key={pageNum}
                variant={pageNum === currentPage ? "default" : "outline"}
                size="sm"
                onClick={() => handlePageChange(pageNum)}
              >
                {pageNum}
              </Button>
            )
          })}

          {totalPages > 5 && <span className="mx-1">...</span>}

          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>
    )
  }

  if (loading && (!actions || actions.length === 0)) {
    return <div className="p-4 text-center">Loading actions...</div>
  }

  if (error && (!actions || actions.length === 0)) {
    return <div className="p-4 text-center text-red-500">Error: {error}</div>
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          <h2 className="text-2xl font-bold">Actions</h2>

          <form onSubmit={handleSearchSubmit} className="flex w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Search actions..."
                value={searchInputValue}
                onChange={(e) => setSearchInputValue(e.target.value)}
                className="pl-8"
              />
            </div>
            <Button type="submit" className="ml-2">
              Search
            </Button>
          </form>
        </div>

        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center"
          >
            <Filter className="h-4 w-4 mr-1" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>

          <div className="flex items-center space-x-2 text-sm">
            <span>Sort by:</span>
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center"
              onClick={() => handleSortChange("createdOn")}
            >
              Date {renderSortIcon("createdOn")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center"
              onClick={() => handleSortChange("fundingAllocated")}
            >
              Funding {renderSortIcon("fundingAllocated")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center"
              onClick={() => handleSortChange("actionStatus")}
            >
              Status {renderSortIcon("actionStatus")}
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 p-4 border rounded-md bg-gray-50">
            <h3 className="font-medium mb-2">Filter by Status</h3>
            <div className="flex flex-wrap gap-4">
              {["Todo", "InProgress", "Refining", "Completed"].map((status) => (
                <div key={status} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`status-${status}`}
                    checked={statusFilter.includes(status)}
                    onChange={() => handleStatusFilterChange(status)}
                    className="rounded"
                  />
                  <label htmlFor={`status-${status}`} className="text-sm cursor-pointer">
                    {status}
                  </label>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <Button size="sm" onClick={applyFilters}>
                Apply Filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {(() => {
        if (!actions) {
          return (
            <div className="text-center p-8 border rounded-md bg-gray-50">
              <p className="text-gray-500">Actions data is undefined.</p>
            </div>
          )
        }

        if (!Array.isArray(actions)) {
          return (
            <div className="text-center p-8 border rounded-md bg-gray-50">
              <p className="text-gray-500">
                Actions data is not an array. Type: {typeof actions}
                {typeof actions === "object" && `. Keys: ${Object.keys(actions).join(", ")}`}
              </p>
            </div>
          )
        }

        if (actions.length === 0) {
          return (
            <div className="text-center p-8 border rounded-md bg-gray-50">
              <p className="text-gray-500">No actions found matching your criteria.</p>
            </div>
          )
        }

        return (
          <>
            {actions.map(renderActionCard)}
            {renderPagination()}
          </>
        )
      })()}
    </div>
  )
}
